import {ReactNode} from "react";
import PinPadIcon from "../Icons/PinPadIcon";
import SymbolPropaneIcon from "../Icons/SymbolPropaneIcon";
import SymbolElectricMeterIcon from "../Icons/SymbolElectricMeterIcon";
import SymbolGasMeterIcon from "../Icons/SymbolGasMeterIcon";
import SymbolLocalGasStationIcon from "../Icons/SymbolLocalGasStationIcon";
import SymbolValveIcon from "../Icons/SymbolValveIcon";
import SymbolCO2Icon from "../Icons/SymbolCO2Icon";
import SymbolPropaneTankIcon from "../Icons/SymbolPropaneTankIcon";
import SymbolOilBarrelIcon from "../Icons/SymbolOilBarrelIcon";
import SymbolWaterHeaterIcon from "../Icons/SymbolWaterHeaterIcon";
import {Step} from "../../App";

interface AufgabeProps {
    numpad: string,
    step: Step,
    language?: string,
    fehler?: string,
}


enum langKey {
    subtitleSymbol,
    subtitleAnzeige,
    subtitleWert,
    subtitleSkala,
    headline,
    abfrageHeadline,
    anzeigenAbfrageFrage,
    wertAbfrageFrage,
    anzeigenAbfrageFrage21,
    anzeigenAbfrageFrage31,
    bestaetigenMitRaute,
    loeschenMitStern,
    anzeigenAuswahlHeadline,
    symbolAbfrageFrage,
    symbolAbfrageSubtitle,
    fehlerMeldung,
    wertAbfrageFrage12,
    wertAbfrageFrage22,
    wertAbfrageFrage41,
    skalaAuswahlTemperatur,
    skalaAuswahlGesamtdruck,
    skalaAbfrageFrage,
    headlineFinale,
    finalAuswahlHeadline,
    finalAbfrageFrage,
    headlineFinaleSubtitle,
}

export default function Aufgabe({fehler, numpad, step, language = 'de'}: AufgabeProps) {
    if (step.type === 'symbol') {
        if (!['1', '2', '3', '4', '5', '6', '7', '8', '9',].includes(numpad)) {
            numpad = '';
        }
    }

    let subtitle = ''

    if (step.type === 'symbol') {
        subtitle = text(langKey.subtitleSymbol, language)
    } else if (step.type === 'anzeige') {
        subtitle = text(langKey.subtitleAnzeige, language)
    } else if (step.type === 'wert') {
        subtitle = text(langKey.subtitleWert, language)
    } else if (step.type === 'skala') {
        subtitle = text(langKey.subtitleSkala, language)
    }

    return <>
        <div className={'grid grid-cols-2 gap-4 p-10'}>
            <div
                className={'bg-w98-gray col-span-full h-24 content-center shadow-sm shadow-w98-white border border-w98-dark-gray'}>
                {step.type !== 'final' && <p className={'text-6xl text-left pl-4 font-bold'}>
                    {text(langKey.headline, language)}
                    <span className={'ml-6 text-4xl'}>({step.schritt}.{step.fortschritt})</span>
                </p>}
                {step.type === 'final' && <p className={'text-6xl text-left pl-4 font-bold'}>
                    {text(langKey.headlineFinale, language)} <span
                    className={'text-4xl'}>({text(langKey.headlineFinaleSubtitle, language)})</span>
                </p>}
            </div>
            <div className={'p-6 bg-w98-gray shadow-sm shadow-w98-white border border-w98-dark-gray'}>
                {step.type === 'symbol' && <SymbolAuswahl numpad={numpad} fehler={fehler}/>}
                {step.type === 'anzeige' && <AnzeigenAuswahl language={language} numpad={numpad}/>}
                {step.type === 'wert' && <WertAuswahl step={step}/>}
                {step.type === 'skala' && <SkalaAuswahl language={language} numpad={numpad} step={step}/>}
                {step.type === 'final' && <FinalAuswahl language={language} numpad={numpad}/>}
            </div>
            <div className={'bg-w98-gray shadow-sm shadow-w98-white border border-w98-dark-gray p-6'}>
                {step.type === 'symbol' && <SymbolAbfrage numpad={numpad} language={language}/>}
                {step.type === 'anzeige' && <AnzeigenAbfrage step={step} language={language}/>}
                {step.type === 'wert' && <WertAbfrage step={step} language={language}/>}
                {step.type === 'skala' && <SkalaAbfrage step={step} language={language}/>}
                {step.type === 'final' && <FinalAbfrage step={step} language={language}/>}
                <p className={'p-4 mt-4  font-bold text-6xl ' + (fehler ? 'text-w98-red' : 'text-w98-gray')}>{text(langKey.fehlerMeldung, language)}</p>
            </div>
        </div>
    </>
}


function AnzeigenAbfrage({language, step}: { language: string, step: Step }) {
    return <>
        <p className={'text-5xl font-bold pl-2'}>{text(langKey.abfrageHeadline, language)}:</p>
        {step.schritt === 2 && step.fortschritt === 1 && <>
            <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.anzeigenAbfrageFrage21, language)}</p>
            <div className={'flex items-center justify-center'}>
                <SymbolCO2Icon className={'fill-w98-dark-gray'} sizeInPixel={150}/>
            </div>
        </>}
        {step.schritt === 3 && step.fortschritt === 1 && <>
            <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.anzeigenAbfrageFrage31, language)}</p>
            <div className={'flex items-center justify-center'}>
                <SymbolWaterHeaterIcon className={'fill-w98-dark-gray'} sizeInPixel={150}/>
            </div>
        </>}
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.anzeigenAbfrageFrage, language)}</p>
        <div className={'flex flex-row gap-28 mt-8 justify-center items-center'}>
            <PinPadIcon className={'fill-w98-dark-gray'} sizeInPixel={120}/>
        </div>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.bestaetigenMitRaute, language)}</p>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.loeschenMitStern, language)}</p>
    </>
}

function FinalAbfrage({language, step}: { language: string, step: Step }) {
    return <>
        <p className={'text-5xl font-bold pl-2'}>{text(langKey.abfrageHeadline, language)}:</p>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.finalAbfrageFrage, language)}</p>
        <div className={'flex flex-row gap-28 mt-8 justify-center items-center'}>
            <PinPadIcon className={'fill-w98-dark-gray'} sizeInPixel={200}/>
        </div>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.bestaetigenMitRaute, language)}</p>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.loeschenMitStern, language)}</p>
    </>
}

function WertAbfrage({language, step}: { language: string, step: Step }) {
    return <>
        <p className={'text-5xl font-bold pl-2'}>{text(langKey.abfrageHeadline, language)}:</p>
        {step.schritt === 1 && step.fortschritt === 2 && <>
            <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.wertAbfrageFrage12, language)}</p>
        </>}
        {step.schritt === 2 && step.fortschritt === 2 && <>
            <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.wertAbfrageFrage22, language)}</p>
        </>}
        {step.schritt === 4 && step.fortschritt === 1 && <>
            <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.wertAbfrageFrage41, language)}</p>
        </>}
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.wertAbfrageFrage, language)}</p>
        <div className={'flex flex-row gap-28 mt-8 justify-center items-center'}>
            <img className={'fill-w98-dark-gray h-[200px]'} alt={'GEAR'} src={'/gear.svg'}/>
        </div>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.bestaetigenMitRaute, language)}</p>
    </>
}

function SkalaAbfrage({language, step}: { language: string, step: Step }) {
    return <>
        <p className={'text-5xl font-bold pl-2'}>{text(langKey.abfrageHeadline, language)}:</p>
        {step.schritt === 3 && step.fortschritt === 2 && <>
            <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.skalaAbfrageFrage, language)}</p>
        </>}
        {step.schritt === 4 && step.fortschritt === 1 && <>
            <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.wertAbfrageFrage41, language)}</p>
        </>}
        <div className={'flex flex-row gap-28 mt-8 justify-center items-center'}>
            <PinPadIcon className={'fill-w98-dark-gray'} sizeInPixel={200}/>
        </div>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.bestaetigenMitRaute, language)}</p>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.loeschenMitStern, language)}</p>
    </>
}


function AnzeigenAuswahl({numpad, language}: { language: string, numpad: string }) {
    return <>
        <div className={'flex flex-col gap-8 justify-center items-center content-center h-full'}>
            <p className={'text-5xl'}>{text(langKey.anzeigenAuswahlHeadline, language)}:</p>
            <div className={'w-72 h-20 bg-w98-white rounded-xl content-center'}>
                <p className={'text-w98-blue bg-w98-white w-72 text-center rounded-xl text-6xl'}>{numpad}</p>
            </div>
        </div>
    </>
}


function FinalAuswahl({numpad, language}: { language: string, numpad: string }) {
    return <>
        <div className={'flex flex-col gap-8 justify-center items-center content-center h-full'}>
            <p className={'text-5xl'}>{text(langKey.finalAuswahlHeadline, language)}:</p>
            <div className={'w-72 h-20 bg-w98-white rounded-xl content-center'}>
                <p className={'text-w98-blue bg-w98-white w-72 text-center rounded-xl text-6xl'}>{numpad}</p>
            </div>
        </div>
    </>
}


function WertAuswahl({step}: { step: Step }) {
    return <>
        <div className={'flex flex-col gap-8 justify-center items-center content-center h-full relative'}>
            {step.schritt === 1 && step.fortschritt === 2 &&
                <img className={'w-2/3'} alt={'WERT'} src={'/Anzeige-4.svg'}/>}
            {step.schritt === 2 && step.fortschritt === 2 &&
                <img className={'w-2/3'} alt={'WERT'} src={'/Anzeige-3.svg'}/>}
            {step.schritt === 3 && step.fortschritt === 2 &&
                <img className={'w-2/3'} alt={'WERT'} src={'/Anzeige-5.svg'}/>}
        </div>
    </>
}


function SkalaAuswahl({numpad, language, step}: { language: string, numpad: string, step: Step }) {
    return <>
        <div className={'flex flex-col gap-8 justify-center items-center content-center h-full'}>
            {step.schritt === 3 && step.fortschritt === 2 &&
                <p className={'text-5xl'}>{text(langKey.skalaAuswahlTemperatur, language)}:</p>}
            {step.schritt === 4 && step.fortschritt === 1 &&
                <p className={'text-5xl'}>{text(langKey.skalaAuswahlGesamtdruck, language)}:</p>}
            <div className={'w-72 h-20 bg-w98-white rounded-xl content-center'}>
                <p className={'text-w98-blue bg-w98-white w-72 text-center rounded-xl text-6xl'}>{numpad}</p>
            </div>
        </div>
    </>
}

function SymbolAuswahl({numpad, fehler}: { numpad: string, fehler: string }) {
    return <div className={'grid grid-cols-3 gap-8'}>
        <SymbolBox title={'1'} numpad={numpad}><SymbolPropaneIcon
            className={(numpad === '1' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'2'} numpad={numpad}><SymbolElectricMeterIcon
            className={(numpad === '2' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'3'} numpad={numpad}><SymbolGasMeterIcon
            className={(numpad === '3' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'4'} numpad={numpad}><SymbolLocalGasStationIcon
            className={(numpad === '4' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'5'} numpad={numpad}><SymbolValveIcon
            className={(numpad === '5' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'6'} numpad={numpad}><SymbolCO2Icon
            className={(numpad === '6' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'7'} numpad={numpad}><SymbolPropaneTankIcon
            className={(numpad === '7' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'8'} numpad={numpad}><SymbolOilBarrelIcon
            className={(numpad === '8' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
        <SymbolBox title={'9'} numpad={numpad}><SymbolWaterHeaterIcon
            className={(numpad === '9' ? 'fill-w98-blue p-4' : 'fill-w98-dark-gray p-4')
            }/></SymbolBox>
    </div>
}


function SymbolAbfrage({numpad, language}: { numpad: string, language: string }) {
    return <>
        <p className={'text-5xl font-bold pl-2'}>{text(langKey.abfrageHeadline, language)}:</p>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.symbolAbfrageSubtitle, language)}: <span
            className={'ml-4 font-mono'}>_ _ 5 0 _</span></p>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.symbolAbfrageFrage, language)}</p>
        <div className={'flex flex-row gap-28 mt-8 justify-center items-center'}>
            <PinPadIcon className={'fill-w98-dark-gray'} sizeInPixel={200}/>
            <div className={'bg-w98-white w-50 h-50 rounded-xl content-center shadow-sm shadow-w98-dark-gray'}>
                <p className={'text-8xl font-bold text-center'}>{numpad}</p>
            </div>
        </div>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.bestaetigenMitRaute, language)}</p>
        <p className={'text-3xl font-medium pl-4 mt-8'}>{text(langKey.loeschenMitStern, language)}</p>
    </>
}


function SymbolBox({numpad, title, children}: { numpad: string, title: string, children?: ReactNode }) {
    return <>
        <div className={'flex flex-col justify-center items-center'}>
            <div
                className={'bg-w98-white w-40 h-40 rounded-xl content-center shadow-sm shadow-w98-dark-gray border-w98-blue ' + (numpad === title ? 'border-4' : '')}>
                {children}
            </div>
            <p className={'text-4xl text-center mt-2 ' + (numpad === title ? 'font-bold' : '')}>{title}</p>
        </div>
    </>
}

function text(key: langKey, language: string): string {
    if (key === langKey.subtitleSymbol) {
        if (language === 'en') {
            return 'Select correct sign/type';
        } else {
            return 'Symbol/Typ auswählen';
        }
    }
    if (key === langKey.subtitleAnzeige) {
        if (language === 'en') {
            return 'Enter display number';
        } else {
            return 'Anzeigennummer eingeben';
        }
    }
    if (key === langKey.subtitleWert) {
        if (language === 'en') {
            return 'Enter correct value';
        } else {
            return 'Wert einstellen';
        }
    }
    if (key === langKey.subtitleSkala) {
        if (language === 'en') {
            return 'Enter correct value';
        } else {
            return 'Wert einstellen';
        }
    }
    if (key === langKey.headline) {
        if (language === 'en') {
            return 'Synchronizing systems';
        } else {
            return 'Systeme synchronisieren';
        }
    }
    if (key === langKey.abfrageHeadline) {
        if (language === 'en') {
            return 'Query';
        } else {
            return 'Abfrage';
        }
    }
    if (key === langKey.anzeigenAbfrageFrage) {
        if (language === 'en') {
            return 'Enter the correct display number. Use the number pad for input.';
        } else {
            return 'Gib die korrekte Nummer der Messanzeige im Nummernfeld ein.';
        }
    }
    if (key === langKey.wertAbfrageFrage) {
        if (language === 'en') {
            return 'Set the correct value using the red valve wheel.';
        } else {
            return 'Stelle den korrekten Wert über das rote Ventilrad ein.';
        }
    }
    if (key === langKey.anzeigenAbfrageFrage21) {
        if (language === 'en') {
            return 'The display of the CO2-meters must be synchronized.';
        } else {
            return 'Die Anzeige des CO2-Messers muss synchronisiert werden.';
        }
    }
    if (key === langKey.anzeigenAbfrageFrage31) {
        if (language === 'en') {
            return 'The temprature sensor must be calibrated.';
        } else {
            return 'Der Temperatursensor muss kalibriert werden.';
        }
    }
    if (key === langKey.bestaetigenMitRaute) {
        if (language === 'en') {
            return 'Confirm the entry with #';
        } else {
            return 'Bestätige die Eingabe mit #';
        }
    }
    if (key === langKey.loeschenMitStern) {
        if (language === 'en') {
            return 'Delete the entry with *';
        } else {
            return 'Lösche die Eingabe mit *';
        }
    }
    if (key === langKey.anzeigenAuswahlHeadline) {
        if (language === 'en') {
            return 'Display number';
        } else {
            return 'Anzeigennummer';
        }
    }
    if (key === langKey.symbolAbfrageFrage) {
        if (language === 'en') {
            return 'Choose the correct sign. Use the number pad for input.';
        } else {
            return 'Wähle das korrekte Symbol aus. Gib zur Auswahl die Nummer im Nummernfeld ein.';
        }
    }
    if (key === langKey.symbolAbfrageSubtitle) {
        if (language === 'en') {
            return 'Which type has the display with the following number?';
        } else {
            return 'Von welchem Typ ist die Messanzeige mit folgender Nummer?';
        }
    }
    if (key === langKey.fehlerMeldung) {
        if (language === 'en') {
            return 'Given value is wrong!';
        } else {
            return 'Eingabe ist falsch!';
        }
    }
    if (key === langKey.wertAbfrageFrage12) {
        if (language === 'en') {
            return 'Transfer the value of the display.';
        } else {
            return 'Übertragen Sie den Wert der Anzeige.';
        }
    }
    if (key === langKey.wertAbfrageFrage22) {
        if (language === 'en') {
            return 'Transfer the value of the display.';
        } else {
            return 'Übertragen Sie den Wert der Anzeige.';
        }
    }
    if (key === langKey.wertAbfrageFrage41) {
        if (language === 'en') {
            return 'Adjust the overall air pressure of the airlock. It is calculated from three individual values.';
        } else {
            return 'Stellen Sie den Gesamtluftdruck in der Schleuse ein. Er berechnet sich aus drei Einzelwerten.';
        }
    }
    if (key === langKey.skalaAuswahlTemperatur) {
        if (language === 'en') {
            return 'Temperature delta';
        } else {
            return 'Temperaturdifferenz';
        }
    }
    if (key === langKey.skalaAuswahlGesamtdruck) {
        if (language === 'en') {
            return 'Overall pressure in airlock';
        } else {
            return 'Gesamtdruck in der Schleuse';
        }
    }
    if (key === langKey.skalaAbfrageFrage) {
        if (language === 'en') {
            return 'By what value must the temperature on the meter display be reduced to reach 38°C?' +
                '\n' + 'Enter the result in the number pad.';
        } else {
            return 'Um welchen Wert muss die Temperatur auf der Anzeige reduziert werden, um 38°C zu erreichen?' +
                '\n' + 'Gib das Ergebnis im Nummernfeld ein.';
        }
    }
    if (key === langKey.headlineFinale) {
        if (language === 'en') {
            return 'Final authentication';
        } else {
            return 'Finale Authentifizierung';
        }
    }
    if (key === langKey.headlineFinaleSubtitle) {
        if (language === 'en') {
            return 'Open door';
        } else {
            return 'Tür öffnen';
        }
    }
    if (key === langKey.finalAuswahlHeadline) {
        if (language === 'en') {
            return 'Airlock door access number';
        } else {
            return 'Zugangsnummer Schleusentür';
        }
    }
    if (key === langKey.finalAbfrageFrage) {
        if (language === 'en') {
            return 'Enter the airlock door access number for the final authentication into the number pad.';
        } else {
            return 'Gib zur finalen Authentifizierung die Zugangsnummer in der Schleusentür in das Nummernfeld ein.';
        }
    }

    return '';
}